import { useRouter } from "next/router";
import PropTypes from "prop-types";

import { LANG_TOGGLE_LIST } from "@/constants/Language";
import NavigationTabs from "@/view/NavigationTabs";
import PanelHeader from "@/view/PanelHeader";
import useReadingSkillsContext from "@/view/utils/useReadingSkillsContext/useReadingSkillsContext";
import QuizOverviewContainer from "./QuizOverviewContainer";
import QuizPreviewContainer from "./QuizPreviewContainer";
import {
  headerTextContainerCss,
  headerTextCss,
  subHeaderTextCss,
} from "./ReadingSkillsQuizContainer.style";

const ELA_QUIZ_SUBHEADER = "ELA";

const propTypes = {
  closeButtonEventProperties: PropTypes.object,
  data: PropTypes.object,
  isPresentModeActive: PropTypes.bool,
  onClickCloseButton: PropTypes.func,
  shouldFocusCloseButtonRef: PropTypes.object,
  language: PropTypes.oneOf(LANG_TOGGLE_LIST),
};

const ReadingSkillsQuizContainer = ({
  closeButtonEventProperties,
  data,
  isPresentModeActive,
  onClickCloseButton,
  shouldFocusCloseButtonRef,
  language,
}) => {
  const router = useRouter();
  const {
    pathname,
    query,
    query: { activityPanel, levelId, quizId },
  } = router;

  const { quizQuestionMetadata } = useReadingSkillsContext();

  const onClickBackButton = () => {
    const query = { ...router.query };
    // Remove all query params associated with the quiz.
    delete query.activityPanel;
    delete query.question;
    delete query.quizId;
    // Without the quiz query params, the user will return to the activity container.
    router.replace({ pathname: router.pathname, query }, null, {
      shallow: true,
    });
  };

  const handleTabClick = ({ params }) => {
    const href = {
      pathname,
      query: {
        ...query,
        ...params,
      },
    };
    router.replace(href, null, { shallow: true });
  };

  const renderQuizOverview = () => {
    if (!quizId || activityPanel !== "quizOverview") {
      return null;
    }

    return (
      <QuizOverviewContainer
        quizData={data}
        quizQuestionMetadata={quizQuestionMetadata}
      />
    );
  };

  const renderQuizPreview = () => {
    if (!quizId || activityPanel !== "quizPreview") {
      return null;
    }

    return (
      <QuizPreviewContainer
        quizData={data}
        quizQuestionMetadata={quizQuestionMetadata}
        isPresentModeActive={isPresentModeActive}
        language={language}
      />
    );
  };

  const backButtonEventProperties = {
    objectType: "Quiz",
    actionName: "Navigate",
    streamProperties: {
      content_type: "Article",
      description: "Navigate back to teacher activity container from quiz",
      content_id: levelId,
      object_id: quizId,
    },
  };

  return (
    <>
      <PanelHeader
        backButtonEventProperties={backButtonEventProperties}
        closeButtonEventProperties={closeButtonEventProperties}
        onClickBackButton={onClickBackButton}
        onClickCloseButton={onClickCloseButton}
        shouldFocusCloseButtonRef={shouldFocusCloseButtonRef}
        shouldRemoveBorderBottom
      >
        <div css={headerTextContainerCss}>
          <h6 css={subHeaderTextCss}>{ELA_QUIZ_SUBHEADER}</h6>
          <h2 css={headerTextCss}>Quiz</h2>
        </div>
      </PanelHeader>
      <NavigationTabs
        activityPanelQueryParam={activityPanel}
        handleTabClick={handleTabClick}
        levelId={levelId}
        quizId={quizId}
      />
      {renderQuizOverview()}
      {renderQuizPreview()}
    </>
  );
};

ReadingSkillsQuizContainer.propTypes = propTypes;

export default ReadingSkillsQuizContainer;
